import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import type { HULProposalContent, ProposalLoadingState } from '../types';
import {
  loadProposalContent,
  getProposalIdFromUrl,
  verifyPassword,
  getDaysUntilExpiry,
} from '../utils';

interface ProposalContextValue {
  state: ProposalLoadingState;
  proposalId: string | null;
  daysUntilExpiry: number | null;
  reload: () => Promise<void>;
  verifyAndLoad: (password: string) => Promise<boolean>;
}

const ProposalContext = createContext<ProposalContextValue | null>(null);

interface ProposalProviderProps {
  children: ReactNode;
  proposalId?: string;
}

export function ProposalProvider({ children, proposalId: propId }: ProposalProviderProps) {
  const [proposalId] = useState<string | null>(() => propId || getProposalIdFromUrl());
  const [state, setState] = useState<ProposalLoadingState>({ status: 'loading' });
  const [daysUntilExpiry, setDaysUntilExpiry] = useState<number | null>(null);

  const loadContent = useCallback(async () => {
    setState({ status: 'loading' });
    const result = await loadProposalContent(proposalId);
    setState(result);

    if (result.status === 'loaded') {
      setDaysUntilExpiry(getDaysUntilExpiry(result.data.proposal.validUntil));
    }
  }, [proposalId]);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  const reload = useCallback(async () => {
    await loadContent();
  }, [loadContent]);

  const verifyAndLoad = useCallback(async (password: string): Promise<boolean> => {
    if (!proposalId) return false;

    const result = await verifyPassword(proposalId, password);
    if (result.valid && result.data) {
      setState({ status: 'loaded', data: result.data });
      setDaysUntilExpiry(getDaysUntilExpiry(result.data.proposal.validUntil));
      return true;
    }
    return false;
  }, [proposalId]);

  return (
    <ProposalContext.Provider
      value={{
        state,
        proposalId,
        daysUntilExpiry,
        reload,
        verifyAndLoad,
      }}
    >
      {children}
    </ProposalContext.Provider>
  );
}

export function useProposal(): ProposalContextValue {
  const context = useContext(ProposalContext);
  if (!context) {
    throw new Error('useProposal must be used within a ProposalProvider');
  }
  return context;
}

/**
 * Hook to access proposal data directly
 * Throws if proposal is not loaded
 */
export function useProposalData(): HULProposalContent {
  const { state } = useProposal();
  if (state.status !== 'loaded') {
    throw new Error('Proposal data not available');
  }
  return state.data;
}
